import type { InsuranceVerificationFormFields } from "../form/validation.zod";
import {
  ConditionalDisplayManager,
  type InsuranceVerificationFormData,
} from "../utils/conditionalDisplay";
import { FieldConverter } from "../utils/fieldUtils";

// biome-ignore lint/complexity/noStaticOnlyClass: used as a namespace across callers; converting changes every call site
export class InsuranceVerificationDataService {
  static transformFormToUpdate(
    formData: InsuranceVerificationFormFields,
    verification:
      | InsuranceVerificationMessage
      | InsuranceVerificationUpdateMessage,
    options: {
      includeFormCompletion?: boolean;
      explicitStatus?: VerificationStatus;
      explicitOutcome?: VerificationOutcome;
    } = {},
  ): InsuranceVerificationUpdateMessage {
    const payload: InsuranceVerificationUpdateMessage = {
      wieldyId: verification.wieldyId,
      practiceId: verification.practiceId,
    };

    const updatedPayload = InsuranceVerificationDataService.applyFieldUpdates(
      payload,
      formData,
      verification,
    );

    const finalPayload = InsuranceVerificationDataService.applyStatusUpdates(
      updatedPayload,
      options,
    );
    const { appointmentDatetime } =
      verification as Partial<InsuranceVerificationMessage>;
    const isCoverageTerminated = ConditionalDisplayManager.isCoverageTerminated(
      appointmentDatetime ? new Date(appointmentDatetime) : undefined,
      new Date(formData.terminationDate),
    );
    if (isCoverageTerminated) {
      return {
        ...finalPayload,
        verificationOutcome: "COVERAGE_INACTIVE" as VerificationOutcome,
      };
    }

    return finalPayload;
  }

  static transformForFormDisplay(
    verification:
      | InsuranceVerificationMessage
      | InsuranceVerificationUpdateMessage,
  ): Partial<InsuranceVerificationFormFields> {
    return {
      hasOrthodonticCoverage: FieldConverter.booleanToStringWithSpecialCase(
        verification.hasOrthodonticCoverage,
      ),
      orthoManualOrAutomaticPayments:
        verification.orthoManualOrAutomaticPayments ?? undefined,
      terminationDate: verification.terminationDate ?? undefined,
      effectiveDate: verification.effectiveDate ?? undefined,
      orthoPaymentFrequency: verification.orthoPaymentFrequency ?? undefined,
      orthoLifetimeMaxBenefit:
        verification.orthoLifetimeMaxBenefit ?? undefined,
      orthoInitialPaymentPercentage:
        verification.orthoInitialPaymentPercentage ?? undefined,
      orthoAmountUsed: verification.orthoAmountUsed ?? undefined,
      hasOrthoPlanDeductible:
        FieldConverter.booleanToString(
          typeof verification.hasOrthoPlanDeductible === "boolean"
            ? verification.hasOrthoPlanDeductible
            : FieldConverter.stringToBoolean(
                verification.hasOrthoPlanDeductible as string,
              ),
        ) || undefined,
      orthoAmountRemaining: verification.orthoAmountRemaining ?? undefined,
      orthoDeductibleRemaining:
        verification.orthoDeductibleRemaining ?? undefined,
      orthoPercentagePlanPays:
        verification.orthoPercentagePlanPays ?? undefined,
      hasOrthoWaitingPeriod:
        FieldConverter.booleanToString(
          typeof verification.hasOrthoWaitingPeriod === "boolean"
            ? verification.hasOrthoWaitingPeriod
            : FieldConverter.stringToBoolean(
                verification.hasOrthoWaitingPeriod as string,
              ),
        ) || undefined,
      orthoAgeLimit: verification.orthoAgeLimit ?? undefined,
      orthoDateOfWaitingPeriod:
        verification.orthoDateOfWaitingPeriod ?? undefined,
      coordinationOfBenefits: verification.coordinationOfBenefits ?? undefined,
      notes: verification.notes ?? undefined,
      preAuthorizationRequired:
        verification.preAuthorizationRequired ?? undefined,
      groupAcceptsAssignmentOfBenefits:
        verification.groupAcceptsAssignmentOfBenefits ?? undefined,
      callReferenceNumber: verification.callReferenceNumber ?? undefined,
      verificationMethod: verification.verificationMethod ?? undefined,
    };
  }

  static createSkipPayload(
    verification:
      | InsuranceVerificationMessage
      | InsuranceVerificationUpdateMessage,
    skipAction: SkipActionType,
  ): InsuranceVerificationUpdateMessage {
    return {
      wieldyId: verification.wieldyId,
      practiceId: verification.practiceId,
      verificationStatus: "SKIPPED",
      verificationOutcome: skipAction,
      verifiedAt: new Date().toISOString(),
    };
  }

  static createCarrierContactVerifyRequest(
    verification:
      | InsuranceVerificationMessage
      | InsuranceVerificationUpdateMessage,
    carrierContact: Exclude<CarrierContact, "CARRIER_AVAILABLE">,
  ): VerifyInsuranceRequest {
    return {
      wieldyId: verification.wieldyId,
      status: "COMPLETED",
      outcome: carrierContact,
    };
  }

  private static hasChanged(
    newValue: string | number | boolean | null | undefined,
    originalValue: string | number | boolean | null | undefined,
  ): boolean {
    if (newValue === null || newValue === undefined) {
      return originalValue !== null && originalValue !== undefined;
    }
    if (originalValue === null || originalValue === undefined) {
      return newValue !== null && newValue !== undefined;
    }
    return String(newValue) !== String(originalValue);
  }

  static prepareConditionalDisplayData(
    formData: InsuranceVerificationFormFields,
    appointmentDatetime?: string | null,
  ): InsuranceVerificationFormData {
    return {
      termination_date: formData.terminationDate
        ? new Date(formData.terminationDate)
        : null,
      appointment_datetime: appointmentDatetime
        ? new Date(appointmentDatetime)
        : null,
      effective_date: formData.effectiveDate
        ? new Date(formData.effectiveDate)
        : null,
      has_orthodontic_coverage: FieldConverter.stringToBoolean(
        formData.hasOrthodonticCoverage,
      ),
      ortho_lifetime_max_benefit: formData.orthoLifetimeMaxBenefit,
      ortho_amount_used: formData.orthoAmountUsed,
      ortho_amount_remaining: formData.orthoAmountRemaining,
      ortho_percentage_plan_pays: formData.orthoPercentagePlanPays,
      ortho_age_limit: formData.orthoAgeLimit,
      ortho_manual_or_automatic_payments:
        formData.orthoManualOrAutomaticPayments,
      ortho_payment_frequency: formData.orthoPaymentFrequency,
      ortho_initial_payment_percentage: formData.orthoInitialPaymentPercentage,
      has_ortho_deductible_plan: FieldConverter.stringToBoolean(
        formData.hasOrthoPlanDeductible,
      ),
      ortho_deductible_remaining: formData.orthoDeductibleRemaining,
      has_ortho_waiting_period: FieldConverter.stringToBoolean(
        formData.hasOrthoWaitingPeriod,
      ),
      ortho_waiting_period_date: formData.orthoDateOfWaitingPeriod
        ? new Date(formData.orthoDateOfWaitingPeriod)
        : null,
      coordination_of_benefits: formData.coordinationOfBenefits,
      notes: formData.notes,
      pre_authorization_required: formData.preAuthorizationRequired ?? null,
      group_accepts_assignment_of_benefits:
        formData.groupAcceptsAssignmentOfBenefits ?? null,
      call_reference_number: formData.callReferenceNumber ?? null,
      verification_method: formData.verificationMethod ?? null,
    };
  }

  static transformFormToVerifyRequest(
    formData: Partial<InsuranceVerificationFormFields>,
    wieldyId: string,
    options: {
      explicitStatus: VerificationStatus;
      explicitOutcome: VerificationOutcome;
      verificationMethod?: string | null;
      callReferenceNumber?: string | null;
    },
  ): VerifyInsuranceRequest {
    return {
      wieldyId,
      status: options.explicitStatus,
      outcome: options.explicitOutcome,
      ...(options.verificationMethod
        ? { method: options.verificationMethod }
        : {}),
      hasOrthodonticCoverage: formData.hasOrthodonticCoverage || null,
      effectiveDate: formData.effectiveDate ?? null,
      terminationDate: formData.terminationDate ?? null,
      orthoLifetimeMaxBenefit: formData.orthoLifetimeMaxBenefit ?? null,
      orthoAmountUsed: formData.orthoAmountUsed ?? null,
      orthoAmountRemaining: formData.orthoAmountRemaining ?? null,
      orthoPercentagePlanPays: formData.orthoPercentagePlanPays ?? null,
      orthoAgeLimit: formData.orthoAgeLimit ?? null,
      orthoManualOrAutomaticPayments:
        formData.orthoManualOrAutomaticPayments || null,
      orthoPaymentFrequency: formData.orthoPaymentFrequency || null,
      orthoInitialPaymentPercentage:
        formData.orthoInitialPaymentPercentage ?? null,
      hasOrthoPlanDeductible:
        typeof formData.hasOrthoPlanDeductible === "boolean"
          ? formData.hasOrthoPlanDeductible
          : (FieldConverter.stringToBoolean(formData.hasOrthoPlanDeductible) ??
            null),
      orthoDeductibleRemaining: formData.orthoDeductibleRemaining ?? null,
      hasOrthoWaitingPeriod:
        typeof formData.hasOrthoWaitingPeriod === "boolean"
          ? formData.hasOrthoWaitingPeriod
          : (FieldConverter.stringToBoolean(formData.hasOrthoWaitingPeriod) ??
            null),
      orthoDateOfWaitingPeriod: formData.orthoDateOfWaitingPeriod ?? null,
      coordinationOfBenefits: formData.coordinationOfBenefits ?? null,
      preAuthorizationRequired: formData.preAuthorizationRequired || null,
      groupAcceptsAssignmentOfBenefits:
        formData.groupAcceptsAssignmentOfBenefits || null,
      notes: formData.notes ?? null,
      callReferenceNumber:
        options.callReferenceNumber ?? formData.callReferenceNumber ?? null,
    };
  }

  private static applyFieldUpdates(
    payload: InsuranceVerificationUpdateMessage,
    formData: InsuranceVerificationFormFields,
    verification:
      | InsuranceVerificationMessage
      | InsuranceVerificationUpdateMessage,
  ): InsuranceVerificationUpdateMessage {
    const updates: Partial<InsuranceVerificationUpdateMessage> = {};

    if (formData.hasOrthoPlanDeductible !== undefined) {
      const booleanValue = FieldConverter.stringToBoolean(
        formData.hasOrthoPlanDeductible,
      );
      const originalValue =
        typeof verification.hasOrthoPlanDeductible === "boolean"
          ? verification.hasOrthoPlanDeductible
          : FieldConverter.stringToBoolean(
              verification.hasOrthoPlanDeductible as string,
            );
      if (
        InsuranceVerificationDataService.hasChanged(booleanValue, originalValue)
      ) {
        updates.hasOrthoPlanDeductible = booleanValue;
      }
    }

    if (formData.hasOrthoWaitingPeriod !== undefined) {
      const booleanValue = FieldConverter.stringToBoolean(
        formData.hasOrthoWaitingPeriod,
      );
      const originalValue =
        typeof verification.hasOrthoWaitingPeriod === "boolean"
          ? verification.hasOrthoWaitingPeriod
          : FieldConverter.stringToBoolean(
              verification.hasOrthoWaitingPeriod as string,
            );
      if (
        InsuranceVerificationDataService.hasChanged(booleanValue, originalValue)
      ) {
        updates.hasOrthoWaitingPeriod = booleanValue;
      }
    }

    if (
      formData.effectiveDate !== undefined &&
      InsuranceVerificationDataService.hasChanged(
        formData.effectiveDate,
        verification.effectiveDate,
      )
    ) {
      updates.effectiveDate = formData.effectiveDate || null;
    }

    if (
      formData.terminationDate !== undefined &&
      InsuranceVerificationDataService.hasChanged(
        formData.terminationDate,
        verification.terminationDate,
      )
    ) {
      updates.terminationDate = formData.terminationDate || null;
    }

    const orthoWaitingChange =
      formData.orthoDateOfWaitingPeriod !== undefined &&
      InsuranceVerificationDataService.hasChanged(
        formData.orthoDateOfWaitingPeriod,
        verification.orthoDateOfWaitingPeriod,
      );
    if (orthoWaitingChange) {
      updates.orthoDateOfWaitingPeriod =
        formData.orthoDateOfWaitingPeriod || null;
    }

    const numericFields: Array<keyof InsuranceVerificationFormFields> = [
      "orthoLifetimeMaxBenefit",
      "orthoAmountUsed",
      "orthoAmountRemaining",
      "orthoPercentagePlanPays",
      "orthoAgeLimit",
      "orthoInitialPaymentPercentage",
      "orthoDeductibleRemaining",
    ];

    numericFields.forEach((field) => {
      if (formData[field] !== undefined) {
        const stringValue = FieldConverter.numberToString(
          formData[field] as number,
        );
        const originalValue = (verification as Record<string, unknown>)[
          field
        ] as string;

        if (
          InsuranceVerificationDataService.hasChanged(
            stringValue,
            originalValue,
          )
        ) {
          (updates as Record<string, unknown>)[field] = stringValue;
        }
      }
    });

    const stringFields: Array<keyof InsuranceVerificationFormFields> = [
      "orthoManualOrAutomaticPayments",
      "orthoPaymentFrequency",
    ];

    stringFields.forEach((field) => {
      if (formData[field] !== undefined) {
        const value = formData[field];
        const originalValue = (verification as Record<string, unknown>)[
          field
        ] as string;

        if (InsuranceVerificationDataService.hasChanged(value, originalValue)) {
          (updates as Record<string, unknown>)[field] = value || undefined;
        }
      }
    });

    const coordinationChanged = InsuranceVerificationDataService.hasChanged(
      formData.coordinationOfBenefits,
      verification.coordinationOfBenefits,
    );
    if (coordinationChanged) {
      updates.coordinationOfBenefits = formData.coordinationOfBenefits || null;
    }

    if (
      InsuranceVerificationDataService.hasChanged(
        formData.notes,
        verification.notes,
      )
    ) {
      updates.notes = formData.notes || null;
    }

    // Handle nullable fields - use formData (original) to support clearing
    if (
      InsuranceVerificationDataService.hasChanged(
        formData.preAuthorizationRequired,
        verification.preAuthorizationRequired,
      )
    ) {
      updates.preAuthorizationRequired =
        formData.preAuthorizationRequired || null;
    }

    if (
      InsuranceVerificationDataService.hasChanged(
        formData.groupAcceptsAssignmentOfBenefits,
        verification.groupAcceptsAssignmentOfBenefits,
      )
    ) {
      updates.groupAcceptsAssignmentOfBenefits =
        formData.groupAcceptsAssignmentOfBenefits || null;
    }

    if (
      InsuranceVerificationDataService.hasChanged(
        formData.callReferenceNumber,
        verification.callReferenceNumber,
      )
    ) {
      updates.callReferenceNumber = formData.callReferenceNumber || null;
    }

    if (
      InsuranceVerificationDataService.hasChanged(
        formData.verificationMethod,
        verification.verificationMethod,
      )
    ) {
      updates.verificationMethod = formData.verificationMethod || null;
    }

    const orthoChanged =
      formData.hasOrthodonticCoverage !== undefined &&
      InsuranceVerificationDataService.hasChanged(
        formData.hasOrthodonticCoverage,
        verification.hasOrthodonticCoverage,
      );
    if (orthoChanged) {
      updates.hasOrthodonticCoverage =
        formData.hasOrthodonticCoverage as OrthoCoverage;
    }

    return { ...payload, ...updates };
  }

  private static applyStatusUpdates(
    payload: InsuranceVerificationUpdateMessage,
    options: {
      includeFormCompletion?: boolean;
      explicitStatus?: VerificationStatus;
      explicitOutcome?: VerificationOutcome;
    },
  ): InsuranceVerificationUpdateMessage {
    const statusUpdates: Partial<InsuranceVerificationUpdateMessage> = {};

    if (options.includeFormCompletion) {
      statusUpdates.verificationStatus = "COMPLETED";
      statusUpdates.verificationOutcome = "COVERAGE_ACTIVE_HAS_ORTHO";
      statusUpdates.verifiedAt = new Date().toISOString();
    }

    if (options.explicitStatus !== undefined) {
      statusUpdates.verificationStatus = options.explicitStatus;
    }

    if (options.explicitOutcome !== undefined) {
      statusUpdates.verificationOutcome = options.explicitOutcome;
    }

    const finalStatus = statusUpdates.verificationStatus;
    if (finalStatus === "COMPLETED" || finalStatus === "SKIPPED") {
      statusUpdates.verifiedAt = new Date().toISOString();
    }

    return { ...payload, ...statusUpdates };
  }
}
